import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MailCheck, Loader2 } from 'lucide-react';

function dashboardPathForRole(role) {
  if (role === 'client') return '/cliente/dashboard';
  if (role === 'videomaker') return '/videomaker/dashboard';
  if (role === 'admin') return '/admin/dashboard';
  return '/login';
}

const ConfirmarEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeEmailVerification } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    const token = (searchParams.get('token') || '').trim();
    if (!token) {
      setStatus('missing');
      setMessage('Este link não contém um token válido. Use o botão no e-mail ou peça um novo link na página de login.');
      return;
    }
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      setStatus('loading');
      try {
        const { user } = await completeEmailVerification(token);
        setStatus('success');
        setMessage('E-mail confirmado. A redirecionar…');
        const path = dashboardPathForRole(user?.role);
        setTimeout(() => navigate(path, { replace: true }), 800);
      } catch (err) {
        setStatus('error');
        setMessage(err.message || 'Não foi possível confirmar o e-mail.');
      }
    })();
  }, [searchParams, completeEmailVerification, navigate]);

  return (
    <div className="min-h-[min(70vh,32rem)] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
              {status === 'loading' ? (
                <Loader2 className="w-8 h-8 text-primary animate-spin" aria-hidden />
              ) : (
                <MailCheck className="w-8 h-8 text-primary" aria-hidden />
              )}
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent">
              Confirmar e-mail
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {status === 'loading' && 'A validar o seu link…'}
              {status === 'success' && 'Conta ativada'}
              {(status === 'error' || status === 'missing') && 'Algo correu mal'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'loading' && (
              <p className="text-center text-sm text-muted-foreground">
                Aguarde um momento.
              </p>
            )}
            {(status === 'success' || status === 'error' || status === 'missing') && (
              <Alert
                variant={status === 'success' ? 'default' : 'destructive'}
                className={
                  status === 'success'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                    : ''
                }
              >
                <AlertDescription
                  className={
                    status === 'success'
                      ? 'text-emerald-900 dark:text-emerald-100'
                      : ''
                  }
                >
                  {message}
                </AlertDescription>
              </Alert>
            )}
            {status === 'error' && (
              <div className="flex flex-col gap-2">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Ir para o login</Link>
                </Button>
                <Button asChild variant="ghost" className="w-full">
                  <Link to="/cadastro">Criar conta</Link>
                </Button>
              </div>
            )}
            {status === 'missing' && (
              <Button asChild className="w-full">
                <Link to="/login">Ir para o login</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ConfirmarEmail;
